import MUIContainer from '@/components/MUI-Components/MUI-Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import React, {memo} from 'react';
import parse from 'html-react-parser';
import {fadeInUp} from '@/animation/scroll-animation';
import dynamic from 'next/dynamic';
import DisplayImage from '../display-image';
import {contentMaxWidth, colors, fontSize, fontWeight, letterSpacing, lineHeight, spacing} from '@/styles/tokens';
import Eyebrow from '@/components/Common/Eyebrow';

const AnimatedComponent = dynamic(() => import('../AnimatedComponent').then((mod) => mod.AnimatedComponent));

interface ChildItem {
    src: any;
    redirect?: string;
    heading: string;
    content: string[];
}

interface Props {
    heading: string;
    subheading?: string;
    /** Optional eyebrow label rendered above the heading */
    eyebrow?: string;
    /** Optional bold sentence rendered below the subheading */
    subSentence?: string;
    contentData: {
        children: ChildItem[];
    };
}

// ─── Sx constants ─────────────────────────────────────────────────────────────
const cardBaseSx = {
    pt: {xs: spacing[40], sm: spacing[16]},
    height: '100%',
    backgroundColor: 'transparent',
    position: 'relative' as const
} as const;

const titleSx = {
    fontWeight: fontWeight.bold,
    fontSize: fontSize[16],
    lineHeight: lineHeight[24]
} as const;

const textSx = {
    fontSize: fontSize[16],
    letterSpacing: letterSpacing.normal,
    color: colors.text.primary,
    lineHeight: lineHeight[28]
} as const;

const subSentenceSx = {
    fontWeight: fontWeight.bold,
    fontSize: fontSize[16],
    lineHeight: lineHeight[28],
    color: colors.text.primary,
    mt: spacing[8]
} as const;

const dividerAfterSx = {
    content: '""',
    position: 'absolute',
    top: '15%',
    right: 0,
    height: '70%',
    width: '1px',
    backgroundColor: colors.neutral.borderFaint,
    display: {xs: 'none', md: 'block'}
} as const;

const sourceLabelSx = {
    fontWeight: fontWeight.bold
} as const;

const Growth = ({heading, subheading, eyebrow, subSentence, contentData}: Props) => {
    const totalItems = contentData?.children?.length || 0;

    return (
        <Box>
            <MUIContainer>
                <Box textAlign="center" maxWidth={contentMaxWidth.standard} margin="0 auto" mb={5}>
                    {eyebrow && <Eyebrow text={eyebrow} />}

                    <Typography variant="h1" component="h2" mb={3} fontSize={{xs: fontSize[32], sm: fontSize[48]}} className="font-headers">
                        {heading}
                    </Typography>

                    {subheading && (
                        <Typography
                            variant="subtitle1"
                            component="p"
                            className="font-regular-text remove-underline"
                            sx={{
                                mb: subSentence ? spacing[4] : {xs: spacing[16], md: spacing[48]},
                                fontSize: fontSize[16],
                                color: colors.text.primary
                            }}
                        >
                            {parse(subheading)}
                        </Typography>
                    )}

                    {subSentence && (
                        <Typography component="p" className="font-headers" sx={{...subSentenceSx, mb: {xs: spacing[16], md: spacing[32]}}}>
                            {subSentence}
                        </Typography>
                    )}
                </Box>

                <Grid container spacing={4}>
                    {contentData?.children?.map((item, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index}>
                            <AnimatedComponent variants={fadeInUp} delay={index * 0.1}>
                                <Box
                                    sx={{
                                        ...cardBaseSx,
                                        '&::after': index !== totalItems - 1 ? dividerAfterSx : {}
                                    }}
                                    borderRadius={4}
                                >
                                    {/* Icon + Heading */}
                                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                                        <Box>
                                            <DisplayImage alt={item.heading} src={item.src} className="" width={45} height={45} />
                                        </Box>
                                        <Typography className="font-headers" variant="h1" component="h3" sx={titleSx}>
                                            {item.heading}
                                        </Typography>
                                    </Box>

                                    {/* Content */}
                                    {item.content?.map((text, i) => (
                                        <Typography key={i} variant="body2" className="remove-underline" mb={1} sx={textSx}>
                                            {text ? parse(text) : ''}
                                        </Typography>
                                    ))}

                                    {/* Source */}
                                    {item.redirect && (
                                        <Typography mt={2} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                            <Typography component="span" className="font-headers" sx={sourceLabelSx}>
                                                Source:
                                            </Typography>
                                            <a href={item.redirect} target="_blank" rel="nofollow" className="font-headers">
                                                {(() => {
                                                    try {
                                                        return new URL(item.redirect).hostname.replace('www.', '');
                                                    } catch {
                                                        return item.redirect;
                                                    }
                                                })()}
                                            </a>
                                        </Typography>
                                    )}
                                </Box>
                            </AnimatedComponent>
                        </Grid>
                    ))}
                </Grid>
            </MUIContainer>
        </Box>
    );
};

export default memo(Growth);
